import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from "rxjs";
import { Provision } from 'src/models/provision';
import { ProvisionChange } from 'src/models/provisionchange';
import { ProductType } from 'src/models/producttype';
import { ProvisionItem } from 'src/models/provisionitem';
import { ProvisionContent, ProvisionContentRow } from 'src/models/provisioncontent';
import { StructureControlState } from 'src/models/structurecontrolstate';
import { StructureControlResponse } from 'src/models/structurecontrolresponse';

const PART_PERIOD_PATTERN = /^[0-9]{4}\.?(?:0[1-9]|1[0-2])\.?[1-4]$/;

@Injectable()
export class ProvisionService {
    // provisions
    private provision: Provision = new Provision();
    private provisionupdate: BehaviorSubject<Provision> = new BehaviorSubject<Provision>(new Provision());

    // changes
    // Note: initialized with null to control saving of changes
    private provisionchangesupdate: BehaviorSubject<Array<ProvisionChange>> = new BehaviorSubject<Array<ProvisionChange>>(null);

    private productType = new ProductType();
    private startobject: string = "EP:1";
    private timeValidity: boolean = false;

    private provisionContent: ProvisionContent | null = null;

    get pendingFpcCount(): number { return this.provision.Changes.filter(change => change.Type === 'FPC').length; }
    get savedPartPeriod(): string { return this.provisionContent?.Details?.PartPeriod || ''; }


    // structure control
    private structureControlStateUpdate: BehaviorSubject<StructureControlState> = new BehaviorSubject<StructureControlState>(new StructureControlState(false, false));
    private structureControlResponse: StructureControlResponse = new StructureControlResponse();
    private structureControlResponseUpdate: BehaviorSubject<StructureControlResponse> = new BehaviorSubject<StructureControlResponse>(null);
    private analysisInputUpdates = new Subject<'edit' | 'reset'>();

    getAnalysisInputUpdates(): Observable<'edit' | 'reset'> {
        return this.analysisInputUpdates.asObservable();
    }

    private invalidateAnalysis(reason: 'edit' | 'reset' = 'edit'): void {
        this.structureControlStateUpdate.next(new StructureControlState(false, false));
        this.analysisInputUpdates.next(reason);
    }

    hasValidAnalysisInputs(): boolean {
        return !!this.provision.Name && PART_PERIOD_PATTERN.test(this.provision.PDVersion || '') && !!this.startobject?.trim();
    }

    isAnalysisSuccessful(): boolean {
        const state = this.structureControlStateUpdate.value;
        return state.Success && !state.Running;
    }

    getProductType() {
        return this.productType;
    }

    insertProductType(data: ProductType) {
        this.productType = data;
    }
    getStartObject() {
        return this.startobject;
    }
    
    insertStartObject(data: string) {
        if (this.startobject === data) { return; }
        this.startobject = data;
        this.invalidateAnalysis();
    }

    //Method for returning timeValidity variable
    getValidityFlag() {
        return this.timeValidity;
    }
    
    //Method for setting timeValidity variable
    insertValidityFlag(data: boolean) {
        if (this.timeValidity === data) { return; }
        this.timeValidity = data;
        this.invalidateAnalysis();
    }
    getProvisionContent(): ProvisionContent | null {
        return this.provisionContent;
    }

    insertProvisionContent(data: ProvisionContent) {
        this.provisionContent = data;
        this.invalidateAnalysis('reset');
    }

    insertPDVersion(PDVersion: string) {
        if (this.provision.PDVersion === PDVersion) { return; }
        this.provision.PDVersion = PDVersion;
        this.provisionupdate.next(this.provision);
        this.invalidateAnalysis();
    }

    getPDVersion() {
        return this.provision.PDVersion;
    }

    getprovisionchangesUpdates(): Observable<Array<ProvisionChange>> {
        return this.provisionchangesupdate.asObservable();
    }

    getProvisionChanges(): Array<ProvisionChange> {
        return this.provision.Changes;
    }

    insertStructureControlState(data: StructureControlState) {
        this.structureControlStateUpdate.next(data);
    }

    getStructureControlStateUpdates(): Observable<StructureControlState> {
        return this.structureControlStateUpdate.asObservable();
    }

    insertStructureControlResponse(data: StructureControlResponse) {
        this.structureControlResponse = data;
        this.structureControlResponseUpdate.next(this.structureControlResponse);
    }

    getStructureControlResponseUpdates(): Observable<StructureControlResponse> {
        return this.structureControlResponseUpdate.asObservable();
    }

    getStructureControlResponse(): StructureControlResponse {
        return this.structureControlResponse;
    }

    insertChange(data: Array<ProvisionChange>) {

        // get change from changes collection
        data.forEach(provisionchange => {
            var change = this.provision.Changes.find(c => c.Family == provisionchange.Family && c.Type == provisionchange.Type);

            if (change) {
                //change
                change.ToExecution = provisionchange.ToExecution;
                change.ToExecutionDescription = provisionchange.ToExecutionDescription;
            }
            else {
                // add new change
                this.provision.Changes.push(provisionchange);
            }
        });


        this.invalidateAnalysis();
        // broadcast the changes, and make a new list to trigger change
        this.provisionchangesupdate.next(Object.assign([], this.provision.Changes));
    }

    // Used to replace changes completely after TCR/VCR due to additonal changes.
    updateChanges(changes: Array<ProvisionChange>) {
        this.invalidateAnalysis();
        this.provisionchangesupdate.next(Object.assign([], changes));
    }

    removeChange(data: ProvisionChange) {
        // remove from changes
        const index = this.provision.Changes.indexOf(data);
        if (index < 0) { return; }
        this.provision.Changes.splice(index, 1);

        this.invalidateAnalysis();
        // broadcast the new changes array
        this.provisionchangesupdate.next(Object.assign([], this.provision.Changes));
    }

    insertProvision(data: Provision) {
        this.provision = data;
        this.invalidateAnalysis('reset');
        this.provisionupdate.next(this.provision);
    }
    getProvision(): Provision {
        return this.provision;
    }

    getProvisionUpdates(): Observable<Provision> {
        return this.provisionupdate.asObservable();
    }

    // returns an updated C spec with all changes included
    // use to send to tcr/vcr etc
    getCurrentProvision(): Array<ProvisionItem> {

        // copy all values to new list to not affect the C spec
        let changedSpec = new Array<ProvisionItem>();
        this.provision.Provision.forEach(item => { changedSpec.push(new ProvisionItem(item.Type, item.Family, null, item.Execution, null, false)) });

        this.provision.Changes.forEach(change => {
            if (change.Type === 'FPC') {
                let specFPC = changedSpec.find(i => i.Family === change.Family);

                if (specFPC) {
                    // changed
                    if (change.ToExecution) {
                        specFPC.Execution = change.ToExecution;
                    }

                    // removed
                    if (!change.ToExecution) {
                        changedSpec = changedSpec.filter(x => x.Family != change.Family);
                    }
                }
                else {
                    // added
                    changedSpec.push(new ProvisionItem(change.Type, change.Family, null, change.ToExecution, null, false));
                }
            }

            else if (change.Type === 'CO' || change.Type === 'CCO') {
                changedSpec = changedSpec.filter(x => x.Execution != change.Family);
            }

            else if (change.Type === 'RPC') {
                //RPCs are not in the specification

            }
        });

        return changedSpec;
    }

    getRowsForSave(): ProvisionContentRow[] {
        if (!this.provisionContent) {
            throw new Error('Cannot save a provision that has not been loaded');
        }
        const rows = new Map<string, ProvisionContentRow>();
        for (const change of this.provision.Changes.filter(change => change.Type === 'FPC')) {
            const type = `FPC${change.Family}`;
            rows.set(type, { Type: type, Value: change.ToExecution || '' });
        }
        if (rows.size === 0 && this.getPartPeriodForSave() === undefined) {
            throw new Error('There are no changes to save');
        }
        return [...rows.values()];
    }

    hasPartPeriodChanges(): boolean {
        return this.provisionContent !== null && (this.provision.PDVersion || '') !==
            (this.provisionContent.Details?.PartPeriod || '');
    }

    getPartPeriodForSave(): string | undefined {
        if (!this.hasPartPeriodChanges()) {
            return undefined;
        }
        if (!PART_PERIOD_PATTERN.test(this.provision.PDVersion || '')) {
            throw new Error('Enter a valid part period before saving');
        }
        return this.provision.PDVersion;
    }

    canSaveProvision(): boolean {
        const periodChanged = this.hasPartPeriodChanges();
        return !!this.provisionContent && !!this.provision.Name &&
            this.isAnalysisSuccessful() && this.hasValidAnalysisInputs() &&
            this.hasPendingChanges() &&
            (!periodChanged || PART_PERIOD_PATTERN.test(this.provision.PDVersion || ''));
    }

    hasPendingChanges(): boolean {
        return this.provision.Changes.some(change => change.Type === 'FPC') || this.hasPartPeriodChanges();
    }

    clearData() {
        this.provisionContent = null;
        this.insertProvision(new Provision());
        this.insertProductType(new ProductType());
        this.insertStructureControlResponse(null);
        this.provisionchangesupdate.next(new Array<ProvisionChange>()); // clear subscribed array
    }
}
